import { useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";

const fmtINR = (v) => {
  if (v == null || v === "") return "";
  const abs = Math.abs(v);
  if (abs >= 1_00_00_000) return `${(v / 1_00_00_000).toFixed(1)} Cr`;
  if (abs >= 1_00_000) return `${(v / 1_00_000).toFixed(1)} L`;
  return typeof v === "number" ? v.toLocaleString("en-IN") : v;
};

const COLORS = ["#0051A4", "#38bdf8", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444"];

const SECTIONS = [
  { title: "ASSETS", items: [] },
  { title: "Current Assets", items: [
    { label: "Cash & Equivalents", key: "cash_equivalents" },
    { label: "Accounts Receivable", key: "accounts_receivable" },
    { label: "Inventory", key: "inventory" },
    { label: "Prepaid Expenses", key: "prepaid_expenses" },
  ]},
  { label: "Total Current Assets", key: "total_current_assets", bold: true, highlight: true },
  { title: "Non-Current Assets", items: [
    { label: "Property, Plant & Equipment", key: "ppe_net" },
    { label: "Intangible Assets", key: "intangible_assets" },
    { label: "Investments", key: "investments" },
  ]},
  { label: "Total Non-Current Assets", key: "total_noncurrent_assets", bold: true, highlight: true },
  { label: "TOTAL ASSETS", key: "total_assets", bold: true, highlight: true, divider: true },
  { title: "LIABILITIES", items: [] },
  { title: "Current Liabilities", items: [
    { label: "Accounts Payable", key: "accounts_payable" },
    { label: "Short-term Debt", key: "short_term_debt" },
    { label: "Accrued Expenses", key: "accrued_expenses" },
    { label: "Current Portion Long-term Debt", key: "current_portion_lt_debt" },
  ]},
  { label: "Total Current Liabilities", key: "total_current_liabilities", bold: true, highlight: true },
  { title: "Non-Current Liabilities", items: [
    { label: "Long-term Debt", key: "long_term_debt" },
    { label: "Deferred Tax", key: "deferred_tax" },
  ]},
  { label: "Total Non-Current Liabilities", key: "total_noncurrent_liabilities", bold: true, highlight: true },
  { label: "TOTAL LIABILITIES", key: "total_liabilities", bold: true, highlight: true, divider: true },
  { title: "EQUITY", items: [
    { label: "Common Stock", key: "common_stock" },
    { label: "Retained Earnings", key: "retained_earnings" },
  ]},
  { label: "TOTAL EQUITY", key: "total_equity", bold: true, highlight: true },
];

const flatRows = () => {
  const rows = [];
  for (const sec of SECTIONS) {
    if (sec.title) {
      rows.push({ type: "header", title: sec.title });
      if (sec.items) sec.items.forEach((i) => rows.push({ type: "data", ...i }));
    }
    if (sec.key) rows.push({ type: "data", ...sec });
  }
  return rows;
};

const BalanceSheet = ({ data, endPeriod, comparisonData, comparisonPeriod }) => {
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (!endPeriod) return data;
    const endIdx = parseInt(endPeriod, 10);
    if (!isNaN(endIdx) && endIdx >= 1 && endIdx <= data.length) {
      return data.slice(0, endIdx);
    }
    return data;
  }, [data, endPeriod]);

  const compItem = useMemo(() => {
    if (!comparisonData || comparisonData.length === 0 || !comparisonPeriod || comparisonPeriod === "none") return null;
    const idx = parseInt(comparisonPeriod, 10) - 1;
    return (idx >= 0 && idx < comparisonData.length) ? comparisonData[idx] : comparisonData[comparisonData.length - 1];
  }, [comparisonData, comparisonPeriod]);

  if (!data || data.length === 0 || filteredData.length === 0) return <p className="text-muted-foreground p-4">No data available</p>;

  const latest = filteredData[filteredData.length - 1];
  const rows = flatRows();

  const pieData = [
    { name: "Current Assets", value: latest.total_current_assets },
    { name: "Non-Current Assets", value: latest.total_noncurrent_assets },
    { name: "Current Liabilities", value: latest.total_current_liabilities },
    { name: "Non-Current Liabilities", value: latest.total_noncurrent_liabilities },
    { name: "Equity", value: latest.total_equity },
  ];

  return (
    <div className="space-y-6" data-testid="balance-sheet">
      {/* Composition chart */}
      <div className="h-72 w-full max-w-3xl mx-auto">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={75}
              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              labelLine={false}
            >
              {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => fmtINR(v)} />
            <Legend
              layout="horizontal"
              align="center"
              verticalAlign="bottom"
              wrapperStyle={{ paddingTop: 12, fontSize: 12 }}
              iconType="circle"
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-background z-10 min-w-[220px]">Line Item</TableHead>
              {filteredData.map((p) => (
                <TableHead key={p.period} className="text-right min-w-[110px]">{p.period}</TableHead>
              ))}
              {compItem && (
                <>
                  <TableHead className="text-right min-w-[125px] bg-muted/40 font-bold text-foreground">
                    Comp ({compItem.period})
                  </TableHead>
                  <TableHead className="text-right min-w-[110px] bg-muted/40 font-bold text-foreground">
                    Variance
                  </TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, idx) => {
              const latestVal = row.key ? (latest[row.key] ?? 0) : 0;
              const compVal = (row.key && compItem) ? (compItem[row.key] ?? 0) : 0;
              const variance = (row.key && compItem) ? latestVal - compVal : 0;

              return row.type === "header" ? (
                <TableRow key={idx} className="bg-muted/50">
                  <TableCell colSpan={filteredData.length + 1 + (compItem ? 2 : 0)} className="font-semibold text-xs uppercase tracking-wider text-muted-foreground sticky left-0 bg-muted/50 z-10">
                    {row.title}
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={row.key} className={row.highlight ? "bg-primary/5" : ""} data-testid={`bs-row-${row.key}`}>
                  <TableCell className={`sticky left-0 bg-background z-10 ${row.bold ? "font-semibold" : ""} ${row.highlight ? "bg-primary/5" : ""}`}>
                    {row.bold ? row.label : `  ${row.label}`}
                  </TableCell>
                  {filteredData.map((p) => (
                    <TableCell key={p.period} className={`text-right tabular-nums ${row.bold ? "font-semibold" : ""}`}>
                      {fmtINR(p[row.key])}
                    </TableCell>
                  ))}
                  {compItem && (
                    <>
                      <TableCell className={`text-right tabular-nums bg-muted/20 ${row.bold ? "font-semibold" : ""}`}>
                        {fmtINR(compVal)}
                      </TableCell>
                      <TableCell
                        className={`text-right tabular-nums bg-muted/20 font-medium ${
                          variance > 0 ? "text-emerald-600" : variance < 0 ? "text-red-600" : ""
                        }`}
                      >
                        {variance > 0 ? "+" : ""}{fmtINR(variance)}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default BalanceSheet;
